import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import './App.css';

import AdminMenu from './components/Admin/adminMenu';
import Dashboard from './components/Admin/Dashboard';
import GestioneCategorie from './components/Admin/GestioneCategorie';
import GestioneEcommerce from './components/Admin/GestioneEcommerce';
import GestioneUtenti from './components/Admin/GestioneUtenti';
import GestioneNewsletter from './components/Admin/GestioneNewsletter';
import { NewsletterEditorPage } from './components/Admin/newsletter-editor';
import GestioneBannerContenuti from './components/Admin/content/GestioneBannerContenuti';
import GestioneMagazineContenuti from './components/Admin/content/GestioneMagazineContenuti';
import { ArticleEditorPage } from './components/Admin/content/article-editor';
import GestioneProdotti from './components/Admin/GestioneProdotti';
import ManutenzioneCataloghi from './components/Admin/manutenzione-cataloghi/ManutenzioneCataloghi';
import Sincronizzazione from './components/Admin/sincronizzazione/Sincronizzazione';
import CatalogMappingEditor from './components/Admin/manutenzione-cataloghi/CatalogMappingEditor';
import ModificaEcommerce from './components/Admin/ModificaEcommerce';
import Impostazioni from './components/Admin/Impostazioni';
import GestioneTickets from './components/Admin/GestioneTickets';
import StoricoAzioni from './components/Admin/StoricoAzioni';
import VendorIndexProgress from './components/Admin/VendorIndexProgress';
import { VendorIndexPollingProvider } from './components/Context/vendorIndexPollingContext';
import ProtectedRoute from './components/Context/protectedRoute';
import { usePermissions } from './components/Context/permissionsProvider';
import { getDefaultAdminRoute } from './utils/resolveUserPermissions';
import Login from './components/Admin/Login';
import RecoverPassword from './components/Admin/RecoverPassword';
import { AuthProvider, useAuth } from './components/Context/authProvider';
import { PermissionsProvider } from './components/Context/permissionsProvider';
import { AdminNotificationUnreadProvider } from './components/Context/notificationContext';
import { AdminTicketUnreadProvider } from './components/Context/ticketUnreadContext';
import { UIProvider, useUI } from './components/Context/UIContext';
import { registerErrorDisplay, registerLogoutFn } from './services/axiosConfig';
import { Box, ThemeProvider, Typography } from '@mui/material';
import theme from './theme/adminTheme';

// Componente helper per registrare l'interceptor con il context
const AxiosInterceptorSetup = () => {
  const { showError } = useUI();
  const { logout } = useAuth();
  
  React.useEffect(() => {
    registerErrorDisplay(showError);
  }, [showError]);

  React.useEffect(() => {
    if (logout) {
      registerLogoutFn(logout);
    }
  }, [logout]);
  
  return null;
};

const DefaultRedirect = () => {
  const { featureFlags } = usePermissions();
  const route = getDefaultAdminRoute(featureFlags);
  if (!route) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">
          Nessuna sezione disponibile per il tuo profilo. Usa il menu per effettuare il logout.
        </Typography>
      </Box>
    );
  }
  return <Navigate to={route} replace />;
};

const SincronizzazioneRoute = ({ children }) => {
  const { featureFlags } = usePermissions();
  if (!featureFlags.enableSincronizzazione) {
    const route = getDefaultAdminRoute(featureFlags);
    if (!route) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography color="text.secondary">
            Nessuna sezione disponibile per il tuo profilo.
          </Typography>
        </Box>
      );
    }
    return <Navigate to={route} replace />;
  }
  return children;
};

const ManutenzioneCataloghiRoute = ({ children }) => {
  const { featureFlags } = usePermissions();
  if (!featureFlags.enableManutenzioneCataloghi) {
    const route = getDefaultAdminRoute(featureFlags);
    if (!route) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography color="text.secondary">
            Nessuna sezione disponibile per il tuo profilo.
          </Typography>
        </Box>
      );
    }
    return <Navigate to={route} replace />;
  }
  return children;
};

const LegacyCatalogVendorRedirect = () => {
  const { vendorId } = useParams();
  return <Navigate to={`/manutenzione-cataloghi/operazioni-vendor/${vendorId}`} replace />;
};

const AppLayout = () => {
  const location = useLocation();
  const isLogin = location.pathname === "/login/admin" || location.pathname === "/recupero-password";

  if (isLogin) {
    return (
      <Routes>
        <Route path="/login/admin" element={<Login />} />
        <Route path="/recupero-password" element={<RecoverPassword />} />
        <Route path="*" element={<Navigate to="/login/admin" replace />} />
      </Routes>
    );
  }

  return (
    <VendorIndexPollingProvider>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>
      <AdminMenu activeRoute={location.pathname} />
      {/* pt: 0 sul contenitore di scroll cosi' gli header sticky (top:0) restano a filo;
          lo spazio superiore e' spostato nel wrapper interno che scorre. */}
      <Box component="main" sx={{ flexGrow: 1, px: { xs: 1.5, sm: 2, md: 3 }, pb: { xs: 1.5, sm: 2, md: 3 }, pt: 0, overflowX: 'hidden', overflowY: 'auto', minWidth: 0, width: 0, maxWidth: '100%' }}>
        <Box sx={{ minHeight: '100%', width: '100%', maxWidth: '100%', pt: { xs: 1.5, sm: 2, md: 3 } }}>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/gestione-ecommerce" element={<GestioneEcommerce />} />
              <Route path="/gestione-ecommerce/modifica" element={<ModificaEcommerce />} />
              <Route path="/gestione-utenti" element={<GestioneUtenti />} />
              <Route path="/gestione-tickets" element={<GestioneTickets />} />
              <Route path="/newsletter" element={<GestioneNewsletter />} />
              <Route path="/newsletter/editor" element={<NewsletterEditorPage />} />
              <Route path="/newsletter/editor/:documentId" element={<NewsletterEditorPage />} />
              <Route path="/gestione-contenuti" element={<Navigate to="/gestione-contenuti/banner" replace />} />
              <Route path="/gestione-contenuti/banner" element={<GestioneBannerContenuti />} />
              <Route path="/gestione-contenuti/magazine" element={<GestioneMagazineContenuti />} />
              <Route path="/gestione-contenuti/magazine/editor" element={<ArticleEditorPage />} />
              <Route path="/gestione-contenuti/magazine/editor/:articleId" element={<ArticleEditorPage />} />
              <Route path="/gestione-prodotti" element={<GestioneProdotti />} />
              <Route path="/sincronizzazione" element={<SincronizzazioneRoute><Sincronizzazione /></SincronizzazioneRoute>} />
              <Route path="/manutenzione-cataloghi" element={<ManutenzioneCataloghiRoute><ManutenzioneCataloghi /></ManutenzioneCataloghiRoute>} />
              <Route path="/manutenzione-cataloghi/operazioni-vendor/:vendorId" element={<ManutenzioneCataloghiRoute><CatalogMappingEditor /></ManutenzioneCataloghiRoute>} />
              <Route path="/manutenzione-cataloghi/:vendorId" element={<ManutenzioneCataloghiRoute><LegacyCatalogVendorRedirect /></ManutenzioneCataloghiRoute>} />
              <Route path="/gestione-categorie" element={<GestioneCategorie />} />
              <Route path="/storico-azioni" element={<StoricoAzioni />} />
              <Route path="/impostazioni" element={<Impostazioni />} />
            </Route>
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
        </Box>
      </Box>
      {/* Widget fisso in basso a destra: progresso reindex dei prodotti per vendor */}
      <VendorIndexProgress />
    </Box>
    </VendorIndexPollingProvider>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <UIProvider>
        <AuthProvider>
          <PermissionsProvider>
          <AxiosInterceptorSetup />
          <AdminNotificationUnreadProvider>
          <AdminTicketUnreadProvider>
            <Router>
              <AppLayout />
            </Router>
          </AdminTicketUnreadProvider>
          </AdminNotificationUnreadProvider>
          </PermissionsProvider>
        </AuthProvider>
      </UIProvider>
    </ThemeProvider>
  );
}

export default App;
 